import type { PropsWithChildren, ReactElement } from 'react';
import { useEffect } from 'react';
import '@fastnorth/kraken/ThemeProvider.css';
import { useLocation } from 'react-router-dom';
import deriveTitleFromPath from './utils/deriveTitleFromPath';
import {
  AppLayout,
  type PageBannerProps,
  type PageFooterProps,
  type PageHeaderProps,
  type LayoutSlots,
} from '@fastnorth/kraken/components/page-layout';
import { useTranslation } from 'react-i18next';
import type { PageContextProps } from '@fastnorth/kraken/components/page-context';
import handleUserSession from './utils/handleUserSession';
import useUserLinks from './utils/useUserLinks';
import setUserLocale from '../i18n/utils/setUserLocale';
import type { Locale } from '@fastnorth/i18next';
import { useSession } from '../hooks/SessionProvider';

const DetailsLayout = ({ children }: PropsWithChildren): ReactElement => {
  const { i18n, t } = useTranslation('translation', { keyPrefix: 'karwa.layout' });
  const { pathname } = useLocation();
  const [{ session, clientUuid }] = useSession();
  const { user } = session || {};
  const userLinks = useUserLinks();

  const menuGroups: ReactElement[] = [];

  useEffect(() => {
    document.title = deriveTitleFromPath(pathname, t('search_layout.title'));
  }, [pathname, t]);

  const slots: LayoutSlots = {};

  const bannerProps: PageBannerProps = {
    bannerImage: {
      mobile: '/v3/event-search-background.jpg',
    },
  };

  const pageHeader: PageHeaderProps = {
    hamburgerMenu: {
      navLinks: [],
      menuGroups: menuGroups,
      title: t('header.main_menu'),
      languageSelector: {
        availableLocales: [],
        onValueChange: async (value: string) => await setUserLocale(value as Locale),
        selectedValue: i18n.language,
      },
      userContext: {
        user: user ?? undefined,
        links: userLinks,
        sessionActions: {
          signOut: {
            onClick: () => handleUserSession(user || undefined),
            label: t('header.sign_out'),
          },
          signIn: {
            onClick: () => handleUserSession(user || undefined),
            label: t('header.sign_in'),
          },
        },
      },
    },
  };

  const pageFooter: PageFooterProps = {
    copyRightLabel: t('footer.copyright_label', { year: new Date().getFullYear() }),
    locale: i18n.language as Locale,
    privacyLinkLabel: t('footer.privacy_link_label'),
    termsLinkLabel: t('footer.terms_link_label'),
    additionalInfo: clientUuid ? `# ${clientUuid}` : undefined,
  };

  const brandedBackground = undefined;

  const pageCtx: PageContextProps = {
    name: t('search_layout.header.page_header_title'),
    avatar: undefined,
    media: undefined,
  };

  return (
    <AppLayout
      pageHeader={pageHeader}
      pageFooter={pageFooter}
      bannerProps={bannerProps}
      brandedBackground={brandedBackground}
      pageCtx={pageCtx}
      slots={slots}>
      {children}
    </AppLayout>
  );
};

export default DetailsLayout;
